import type { KeyLightingConfig } from "../components/Keyboard/types";
import {
  customDataToKeyColors,
  DEFAULT_KEY_POSITION_MAP,
  isDisplayableLightingCell,
  lightingMatrixKey,
  lightingMatrixKeyLower,
  lightingMatrixKeyUpper,
} from "./lightingUtils";

/** 星闪双灯位 SDK 网格常见为 12 行（上 6 + 下 6） */
export const XINGSHAN_DOUBLE_LIGHTING_SDK_ROW_COUNT = 12;

export const XINGSHAN_DOUBLE_LIGHTING_UI_ROW_COUNT = 6;

export function isXingshanDoubleLightingSdkGrid(
  data: unknown[] | null | undefined
): boolean {
  return (
    Array.isArray(data) &&
    data.length === XINGSHAN_DOUBLE_LIGHTING_SDK_ROW_COUNT
  );
}

/** 将 6 行矩阵色复制为上下灯双层 key（双灯位 Open 且 SDK 仅回 6 行时） */
function duplicateMatrixKeyColorsForDualSplit(
  base: Record<string, KeyLightingConfig>
): Record<string, KeyLightingConfig> {
  const out: Record<string, KeyLightingConfig> = { ...base };
  Object.entries(base).forEach(([key, cell]) => {
    const matched = /^__m_(\d+)_(\d+)$/.exec(key);
    if (!matched) return;
    const row = Number(matched[1]);
    const col = Number(matched[2]);
    out[lightingMatrixKeyUpper(row, col)] = cell;
    out[lightingMatrixKeyLower(row, col)] = cell;
  });
  return out;
}

/** 6 行矩阵：仅映射到上灯或下灯 half-key */
function mapMatrixKeysToSingleLayer(
  base: Record<string, KeyLightingConfig>,
  layer: "upper" | "lower"
): Record<string, KeyLightingConfig> {
  const out: Record<string, KeyLightingConfig> = {};
  Object.entries(base).forEach(([key, cell]) => {
    const matched = /^__m_(\d+)_(\d+)$/.exec(key);
    if (!matched) return;
    const row = Number(matched[1]);
    const col = Number(matched[2]);
    out[
      layer === "upper"
        ? lightingMatrixKeyUpper(row, col)
        : lightingMatrixKeyLower(row, col)
    ] = cell;
  });
  return out;
}

function appendLayerMatrixKeyColors(
  target: Record<string, KeyLightingConfig>,
  rows: Array<Array<KeyLightingConfig>> | undefined,
  layer: "upper" | "lower"
): void {
  rows?.forEach((row, rowIndex) => {
    row?.forEach((cell, colIndex) => {
      if (!isDisplayableLightingCell(cell)) return;
      const key =
        layer === "upper"
          ? lightingMatrixKeyUpper(rowIndex, colIndex)
          : lightingMatrixKeyLower(rowIndex, colIndex);
      target[key] = cell;
    });
  });
}

/**
 * 星闪双灯位：SDK getLightingCustom → 虚拟键盘 keyColors。
 * - OpenUp / OpenDown：单层 6 行
 * - Open（双灯位）：上下层分别写入 __m_{r}_{c}_up / _dn
 */
export function buildKeyColorsFromXingshanLightingGrid(
  data: Array<Array<KeyLightingConfig>> | null | undefined,
  open: string | undefined,
  positionMap: Record<string, [number, number]> = DEFAULT_KEY_POSITION_MAP
): Record<string, KeyLightingConfig> {
  if (!data?.length) return {};

  const openVal = String(open ?? "Open");

  if (isXingshanDoubleLightingSdkGrid(data)) {
    const upperRows = data.slice(0, XINGSHAN_DOUBLE_LIGHTING_UI_ROW_COUNT);
    const lowerRows = data.slice(XINGSHAN_DOUBLE_LIGHTING_UI_ROW_COUNT);

    if (openVal === "OpenUp") {
      const keyColors: Record<string, KeyLightingConfig> = {};
      appendLayerMatrixKeyColors(keyColors, upperRows, "upper");
      return keyColors;
    }
    if (openVal === "OpenDown") {
      const keyColors: Record<string, KeyLightingConfig> = {};
      appendLayerMatrixKeyColors(keyColors, lowerRows, "lower");
      return keyColors;
    }
    if (openVal === "Open") {
      const keyColors: Record<string, KeyLightingConfig> = {};
      appendLayerMatrixKeyColors(keyColors, upperRows, "upper");
      appendLayerMatrixKeyColors(keyColors, lowerRows, "lower");
      return keyColors;
    }
  }

  const base = customDataToKeyColors(data, positionMap);
  if (openVal === "Open") {
    return duplicateMatrixKeyColorsForDualSplit(base);
  }
  if (openVal === "OpenUp") {
    return mapMatrixKeysToSingleLayer(base, "upper");
  }
  if (openVal === "OpenDown") {
    return mapMatrixKeysToSingleLayer(base, "lower");
  }
  return base;
}

/** 从单层 matrix key 解析行列（不含 _up/_dn 后缀） */
export function parseLightingMatrixKeyRowCol(
  key: string
): { row: number; col: number } | null {
  const matched = /^__m_(\d+)_(\d+)(?:_(?:up|dn))?$/.exec(key);
  if (!matched) return null;
  return { row: Number(matched[1]), col: Number(matched[2]) };
}

export { lightingMatrixKey, lightingMatrixKeyUpper, lightingMatrixKeyLower };
